import { Component, input, ChangeDetectionStrategy, signal, computed, inject, LOCALE_ID } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { ButtonsModule, ChipModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { SVGIcon } from '@progress/kendo-angular-icons';
import { moreVerticalIcon } from '@progress/kendo-svg-icons';
import DataTable from 'datatables.net-dt';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { environment } from 'src/environments/environment';
import { CriticalVulnerabilityAgingResponse } from '../../../../../../../core/models/analytics/vss-models';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

export interface AgingData {
  totalVulnerabilities: number;
  ageRanges: Array<{
    range: string;
    count: number;
    percentage: number;
    patchable: number;
    color: string;
  }>;
}

@Component({
  selector: 'app-critical-vulnerability-aging',
  templateUrl: './critical-vulnerability-aging.component.html',
  styleUrls: ['./critical-vulnerability-aging.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ButtonsModule, ChipModule, DialogsModule, DatatableComponent],
})
export class CriticalVulnerabilityAgingComponent {
  private locale = inject(LOCALE_ID);

  criticalVulnerabilityAgingResponse = input.required<CriticalVulnerabilityAgingResponse>();
  public organizationId = input<number | null>();

  public moreVerticalIcon: SVGIcon = moreVerticalIcon;
  public showPopup = signal<boolean>(false);

  /** No period applies. */
  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null ? `${environment.apiUrl}/v1/vicarius/analytics/criticalvulnerabilityagingdetails(${id})` : '';
  });

  private date = (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : '');

  public datatableColumns = [
    { data: 'cve_uid', name: 'cve_uid', title: 'CVE', type: 'string', render: text },
    { data: 'age_range', name: 'age_range', title: 'Age Range', type: 'string', render: text },
    {
      data: 'age_days',
      name: 'age_days',
      title: 'Age',
      type: 'num',
      render: (value: number) => (value != null ? `${value} days` : ''),
    },
    {
      data: 'is_patchable',
      name: 'is_patchable',
      title: 'Patchable',
      type: 'string',
      render: (value: unknown) => (value ? 'Yes' : 'No'),
    },
    { data: 'detected_at', name: 'detected_at', title: 'Detected At', type: 'string', render: this.date },
    { data: 'last_seen', name: 'last_seen', title: 'Last Seen', type: 'string', render: this.date },
  ];

  getChipThemeColor(color: string): 'success' | 'warning' | 'error' | 'info' {
    switch (color) {
      case 'bg-success':
        return 'success';
      case 'bg-warning':
        return 'warning';
      case 'bg-danger':
        return 'error';
      default:
        return 'info';
    }
  }

  onViewData(): void {
    this.showPopup.set(true);
  }

  close(): void {
    this.showPopup.set(false);
  }
}
